import { mergeMap } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import { ConstantAuthorActions } from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';

export interface ConstantAuthorStateModel {
  copyrightsLoaded: boolean;
  modalitiesLoaded: boolean;
  stainsLoaded: boolean;
}

@State<ConstantAuthorStateModel>({
  name: 'constantAuthor',
  defaults: {
    copyrightsLoaded: false,
    modalitiesLoaded: false,
    stainsLoaded: false,
  }
})
@Injectable()
export class ConstantAuthorState {
  constructor(public store: Store,
              public apiControlService: ApiControlService) {
  }

  //<editor-fold desc="Selectors">
  
  @Selector([
    FlatStates.CopyrightEntityState.typedEntities$,
    FlatStates.NavigationControlState.copyrightIdSlug$,
  ])
  static copyright$(typedEntities: { [id: string]: FlatModels.CopyrightEntityModels.Copyright },
                    copyrightId: string | null) {
    return typedEntities && copyrightId && typedEntities[copyrightId] || null;
  }

  @Selector([
    FlatStates.ModalityEntityState.typedEntities$,
    FlatStates.NavigationControlState.modalityIdSlug$,
  ])
  static modality$(typedEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality },
                   modalityId: string | null) {
    return typedEntities && modalityId && typedEntities[modalityId] || null;
  }

  @Selector([
    FlatStates.StainEntityState.typedEntities$,
    FlatStates.NavigationControlState.stainIdSlug$,
  ])
  static stain$(typedEntities: { [id: string]: FlatModels.StainEntityModels.Stain },
                stainId: string | null) {
    return typedEntities && stainId && typedEntities[stainId] || null;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Copyrights">
  
  @Action(ConstantAuthorActions.GetCopyrightsSuccess)
  getCopyrightsSuccess({patchState}: StateContext<ConstantAuthorStateModel>) {
    patchState({
      copyrightsLoaded: true
    });
  }

  @Action(ConstantAuthorActions.GetCopyrights)
  getCopyrights({getState}: StateContext<ConstantAuthorStateModel>) {
    if (getState().copyrightsLoaded) {
      return;
    }
    return this.apiControlService.sendMessage(
      new ConstantAuthorActions.GetCopyrightsRequest()
    ).pipe(
      mergeMap(() =>
        this.store.dispatch(new ConstantAuthorActions.GetCopyrightsSuccess())
      ),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Modalities">
  
  @Action(ConstantAuthorActions.GetModalitiesSuccess)
  getModalitiesSuccess({patchState}: StateContext<ConstantAuthorStateModel>) {
    patchState({
      modalitiesLoaded: true
    });
  }

  @Action(ConstantAuthorActions.GetModalities)
  getModalities({getState}: StateContext<ConstantAuthorStateModel>) {
    if (getState().modalitiesLoaded) {
      return;
    }
    return this.apiControlService.sendMessage(
      new ConstantAuthorActions.GetModalitiesRequest()
    ).pipe(
      mergeMap(() =>
        this.store.dispatch(new ConstantAuthorActions.GetModalitiesSuccess())
      ),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Stains">
  
  @Action(ConstantAuthorActions.GetStainsSuccess)
  getStainsSuccess({patchState}: StateContext<ConstantAuthorStateModel>) {
    patchState({
      stainsLoaded: true
    });
  }

  @Action(ConstantAuthorActions.GetStains)
  getStains({getState}: StateContext<ConstantAuthorStateModel>) {
    if (getState().stainsLoaded) {
      return;
    }
    return this.apiControlService.sendMessage(
      new ConstantAuthorActions.GetStainsRequest()
    ).pipe(
      mergeMap(() =>
        this.store.dispatch(new ConstantAuthorActions.GetStainsSuccess())
      ),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
